import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react'
import styled from 'styled-components'
import * as echarts from 'echarts'
import ReactECharts from 'echarts-for-react'
import { Box, Flex, Icon, Loading } from '../../atoms'
import {
  GeographicDistributionChartProps,
  DEFAULT_GEOGRAPHIC_DISTRIBUTION_CHART_LABELS,
} from './GeographicDistributionChart.types'
import {
  formatCurrency,
  formatNumber,
} from './GeographicDistributionChart.utils'

const DashboardContainer = styled.div`
  width: 100%;
`

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 26px;
`

const MetricCard = styled(Box)`
  background: white;
  border-radius: 10px;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  text-align: center;
`

const MetricValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 6px 0;
`

const MetricLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 3px;
`

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 26px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`

const ChartContainer = styled(Box)`
  background: white;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  min-height: 320px;
`

export interface GeographicDistributionChartRef {
  exportChart: () => string | null
  exportAllCharts: () => (string | null)[]
}

export const GeographicDistributionChart = forwardRef<
  GeographicDistributionChartRef,
  GeographicDistributionChartProps
>(({ data, loading = false, error = null, labels = {} }, ref) => {
  const chartInstance = useRef<echarts.ECharts | null>(null)
  const chartInstance2 = useRef<echarts.ECharts | null>(null)
  const chartInstance3 = useRef<echarts.ECharts | null>(null)

  useImperativeHandle(ref, () => ({
    exportChart: () => {
      if (chartInstance.current) {
        return chartInstance.current.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff',
        })
      }
      return null
    },
    exportAllCharts: () => [
      chartInstance.current?.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff',
      }) ?? null,
      chartInstance2.current?.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff',
      }) ?? null,
      chartInstance3.current?.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff',
      }) ?? null,
    ],
  }))
  const mergedLabels = {
    ...DEFAULT_GEOGRAPHIC_DISTRIBUTION_CHART_LABELS,
    ...labels,
  }

  const citiesByCountOption = useMemo(() => {
    if (!data || !data.topCities || data.topCities.length === 0) return null

    const sorted = [...data.topCities]
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)

    return {
      title: {
        text: mergedLabels.topCitiesByCount,
        left: 'center',
        textStyle: { fontSize: 14, fontWeight: 600 },
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const d = params[0]
          return [
            '<div style="padding: 8px;">',
            `<strong>${d.name}</strong><br/>`,
            `Transactions: ${formatNumber(d.value)}`,
            '</div>',
          ].join('')
        },
      },
      grid: {
        left: '3%',
        right: '15%',
        bottom: '3%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        axisLabel: {
          formatter: (v: number) => formatNumber(v),
        },
      },
      yAxis: {
        type: 'category',
        data: sorted.map((c) => c.name).reverse(),
        axisLabel: { fontSize: 11 },
      },
      series: [
        {
          type: 'bar',
          data: sorted.map((c) => c.count).reverse(),
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
              { offset: 0, color: '#e3f2fd' },
              { offset: 1, color: '#1976d2' },
            ]),
          },
          label: {
            show: true,
            position: 'right',
            formatter: (params: any) => formatNumber(params.value),
            fontSize: 10,
          },
        },
      ],
    }
  }, [data])

  const citiesByVolumeOption = useMemo(() => {
    if (!data || !data.topCities || data.topCities.length === 0) return null

    const sorted = [...data.topCities]
      .sort((a, b) => b.totalValue - a.totalValue)
      .slice(0, 10)

    return {
      title: {
        text: mergedLabels.topCitiesByVolume,
        left: 'center',
        textStyle: { fontSize: 14, fontWeight: 600 },
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const d = params[0]
          return [
            '<div style="padding: 8px;">',
            `<strong>${d.name}</strong><br/>`,
            `Volume: ${formatCurrency(d.value)}`,
            '</div>',
          ].join('')
        },
      },
      grid: {
        left: '3%',
        right: '15%',
        bottom: '3%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        axisLabel: {
          formatter: (v: number) => formatCurrency(v),
        },
      },
      yAxis: {
        type: 'category',
        data: sorted.map((c) => c.name).reverse(),
        axisLabel: { fontSize: 11 },
      },
      series: [
        {
          type: 'bar',
          data: sorted.map((c) => c.totalValue).reverse(),
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
              { offset: 0, color: '#e8f5e9' },
              { offset: 1, color: '#388e3c' },
            ]),
          },
          label: {
            show: true,
            position: 'right',
            formatter: (params: any) => formatCurrency(params.value),
            fontSize: 10,
          },
        },
      ],
    }
  }, [data])

  const citiesByAvgPriceOption = useMemo(() => {
    if (!data || !data.topCities || data.topCities.length === 0) return null

    const sorted = [...data.topCities]
      .sort((a, b) => b.avgPrice - a.avgPrice)
      .slice(0, 10)

    return {
      title: {
        text: mergedLabels.topCitiesByAvgPrice,
        left: 'center',
        textStyle: { fontSize: 14, fontWeight: 600 },
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const d = params[0]
          return [
            '<div style="padding: 8px;">',
            `<strong>${d.name}</strong><br/>`,
            `Avg Price: ${formatCurrency(d.value)}`,
            '</div>',
          ].join('')
        },
      },
      grid: {
        left: '3%',
        right: '15%',
        bottom: '3%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        axisLabel: {
          formatter: (v: number) => formatCurrency(v),
        },
      },
      yAxis: {
        type: 'category',
        data: sorted.map((c) => c.name).reverse(),
        axisLabel: { fontSize: 11 },
      },
      series: [
        {
          type: 'bar',
          data: sorted.map((c) => c.avgPrice).reverse(),
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
              { offset: 0, color: '#fff3e0' },
              { offset: 1, color: '#e65100' },
            ]),
          },
          label: {
            show: true,
            position: 'right',
            formatter: (params: any) => formatCurrency(params.value),
            fontSize: 10,
          },
        },
      ],
    }
  }, [data])

  if (loading) {
    return (
      <DashboardContainer>
        <Flex justify="center" align="center" style={{ minHeight: '320px' }}>
          <Loading size="large" />
        </Flex>
      </DashboardContainer>
    )
  }

  if (error || !data) {
    return (
      <DashboardContainer>
        <Flex
          justify="center"
          align="center"
          style={{ minHeight: '320px', color: '#999' }}
        >
          {error || mergedLabels.noData}
        </Flex>
      </DashboardContainer>
    )
  }

  const totalVolume = data.topCities.reduce((sum, c) => sum + c.totalValue, 0)
  const totalTransactions =
    data.totalTransactions ||
    data.topCities.reduce((sum, c) => sum + c.count, 0)
  const avgTransactionPrice =
    totalTransactions > 0 ? totalVolume / totalTransactions : 0

  return (
    <DashboardContainer>
      <MetricsGrid>
        <MetricCard>
          <Icon name="map-pin" size="medium" />
          <MetricValue>{data.topCities.length}</MetricValue>
          <MetricLabel>{mergedLabels.activeCities}</MetricLabel>
        </MetricCard>

        <MetricCard>
          <Icon name="building" size="medium" />
          <MetricValue>{formatNumber(totalTransactions)}</MetricValue>
          <MetricLabel>{mergedLabels.totalTransactions}</MetricLabel>
        </MetricCard>

        <MetricCard>
          <Icon name="trending-up" size="medium" />
          <MetricValue>{formatCurrency(avgTransactionPrice)}</MetricValue>
          <MetricLabel>{mergedLabels.avgTransactionPrice}</MetricLabel>
        </MetricCard>

        <MetricCard>
          <Icon name="bar-chart" size="medium" />
          <MetricValue>{formatCurrency(totalVolume)}</MetricValue>
          <MetricLabel>{mergedLabels.totalMarketVolume}</MetricLabel>
        </MetricCard>
      </MetricsGrid>

      <ChartsGrid>
        {citiesByCountOption && (
          <ChartContainer>
            <ReactECharts
              option={citiesByCountOption}
              style={{ height: '360px' }}
              onChartReady={(chart) => {
                chartInstance.current = chart
              }}
            />
          </ChartContainer>
        )}

        {citiesByVolumeOption && (
          <ChartContainer>
            <ReactECharts
              option={citiesByVolumeOption}
              style={{ height: '360px' }}
              onChartReady={(chart) => {
                chartInstance2.current = chart
              }}
            />
          </ChartContainer>
        )}

        {citiesByAvgPriceOption && (
          <ChartContainer>
            <ReactECharts
              option={citiesByAvgPriceOption}
              style={{ height: '360px' }}
              onChartReady={(chart) => {
                chartInstance3.current = chart
              }}
            />
          </ChartContainer>
        )}
      </ChartsGrid>
    </DashboardContainer>
  )
})

GeographicDistributionChart.displayName = 'GeographicDistributionChart'
